import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  notification,
} from "antd";
import { Navigate, useNavigate } from "react-router-dom";
import Header from "views/pages/Header/Header";
import adminUserService from "./adminUserService";
import { useTranslation } from "react-i18next";
import { isAdmin } from "utilities/authSession";
import RouteEnum from "constants/RouteEnum";
import backarrow from "../../../assets/images/backarrow.svg";

const AdminUsers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState({
    licenses: {},
    users: [],
    invitations: [],
    recoveries: [],
  });
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    if (!isAdmin()) {
      setForbidden(true);
      return;
    }
    setLoading(true);
    try {
      setData(await adminUserService.summary());
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) {
        setForbidden(true);
        return;
      }
      notification.error({ message: t("genericError") });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  if (forbidden || !isAdmin()) {
    return <Navigate to={RouteEnum.Extractpdf} replace />;
  }

  const withPassword = (title, operation) => {
    let password = "";
    Modal.confirm({
      title,
      content: (
        <Input.Password
          autoComplete="current-password"
          placeholder={t("password")}
          onChange={(event) => {
            password = event.target.value;
          }}
        />
      ),
      async onOk() {
        await operation(password);
        await load();
      },
    });
  };

  const invite = async () => {
    const values = await form.validateFields();
    await adminUserService.invite(
      {
        email: values.email,
        user_name: values.user_name,
        role: values.role,
        locale: values.locale,
      },
      values.admin_password
    );
    form.resetFields();
    setInviteOpen(false);
    await load();
  };

  const userColumns = [
    { title: t("name"), dataIndex: "user_name" },
    { title: t("email"), dataIndex: "email" },
    { title: t("role"), dataIndex: "role", render: (value) => <Tag>{value}</Tag> },
    {
      title: t("status"),
      dataIndex: "status",
      render: (value) => <Tag color={value === "active" ? "green" : "default"}>{value}</Tag>,
    },
    {
      title: t("actions"),
      render: (_, user) => (
        <Space wrap>
          <Button
            size="small"
            onClick={() =>
              withPassword(t("confirmAdminAction"), (password) =>
                adminUserService.updateUser(
                  user.id,
                  { role: user.role === "admin" ? "operator" : "admin" },
                  password
                )
              )
            }
          >
            {user.role === "admin" ? t("demote") : t("promote")}
          </Button>
          <Button
            size="small"
            danger={user.status === "active"}
            onClick={() =>
              withPassword(t("confirmAdminAction"), (password) =>
                adminUserService.updateUser(
                  user.id,
                  { status: user.status === "active" ? "disabled" : "active" },
                  password
                )
              )
            }
          >
            {user.status === "active" ? t("deactivate") : t("activate")}
          </Button>
        </Space>
      ),
    },
  ];

  const invitationColumns = [
    { title: t("email"), dataIndex: "email" },
    { title: t("name"), dataIndex: "user_name" },
    { title: t("role"), dataIndex: "role" },
    { title: t("status"), dataIndex: "status" },
    { title: t("deliveryStatus"), dataIndex: "delivery_status" },
    { title: t("expiresAt"), dataIndex: "expires_at" },
    {
      title: t("actions"),
      render: (_, invitation) => (
        <Space>
          <Button
            size="small"
            disabled={!["pending", "expired"].includes(invitation.status)}
            onClick={() =>
              withPassword(t("resendInvitation"), (password) =>
                adminUserService.resend(invitation.id, password)
              )
            }
          >
            {t("resend")}
          </Button>
          <Button
            size="small"
            danger
            disabled={invitation.status !== "pending"}
            onClick={() =>
              withPassword(t("revokeInvitation"), (password) =>
                adminUserService.revoke(invitation.id, password)
              )
            }
          >
            {t("revoke")}
          </Button>
        </Space>
      ),
    },
  ];

  const recoveryColumns = [
    { title: t("email"), dataIndex: "email" },
    { title: t("name"), dataIndex: "user_name" },
    { title: t("status"), dataIndex: "status" },
    { title: t("requestedAt"), dataIndex: "requested_at" },
    {
      title: t("actions"),
      render: (_, recovery) => (
        <Space>
          <Button
            size="small"
            type="primary"
            disabled={recovery.status !== "pending_admin_approval"}
            onClick={() =>
              withPassword(t("approveRecovery"), (password) =>
                adminUserService.decideRecovery(
                  recovery.id,
                  "approve",
                  "Approved in admin dashboard",
                  password
                )
              )
            }
          >
            {t("approve")}
          </Button>
          <Button
            size="small"
            danger
            disabled={recovery.status !== "pending_admin_approval"}
            onClick={() =>
              withPassword(t("rejectRecovery"), (password) =>
                adminUserService.decideRecovery(
                  recovery.id,
                  "reject",
                  "Rejected in admin dashboard",
                  password
                )
              )
            }
          >
            {t("reject")}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <section className="extract-pdf-mainsection admin-users-page">
      <Header />
      <div className="page-content p-4">
        <button
          type="button"
          onClick={() => navigate(RouteEnum.Extractpdf)}
          className="handle-add-treatment-btn circle-btn mb-2 d-inline-flex align-items-center"
        >
          <img src={backarrow} alt="" className="me-2 back-arrow" />
          {t("back")}
        </button>
        <div className="d-flex justify-content-between mb-3 align-items-center">
          <h1 className="mb-0">{t("usersAndInvitations")}</h1>
          <Button type="primary" onClick={() => setInviteOpen(true)}>
            {t("inviteUser")}
          </Button>
        </div>
        <Row gutter={16} className="mb-4">
          {["used", "licensed", "available"].map((key) => (
            <Col xs={24} md={8} key={key}>
              <Card title={t(key)}>{data.licenses?.[key] ?? "–"}</Card>
            </Col>
          ))}
        </Row>
        <Tabs
          items={[
            {
              key: "users",
              label: t("users"),
              children: <Table rowKey="id" loading={loading} dataSource={data.users} columns={userColumns} />,
            },
            {
              key: "invitations",
              label: t("invitations"),
              children: <Table rowKey="id" loading={loading} dataSource={data.invitations} columns={invitationColumns} />,
            },
            {
              key: "recoveries",
              label: t("mfaRecovery"),
              children: <Table rowKey="id" loading={loading} dataSource={data.recoveries} columns={recoveryColumns} />,
            },
          ]}
        />
      </div>
      <Modal
        title={t("inviteUser")}
        open={inviteOpen}
        onCancel={() => setInviteOpen(false)}
        onOk={invite}
        className="app-form-modal"
        centered
        width={480}
        destroyOnClose
        okText={t("inviteUser")}
        cancelText={t("cancel")}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="email" label={t("email")} rules={[{ required: true, type: "email" }]}>
            <Input autoComplete="email" />
          </Form.Item>
          <Form.Item name="user_name" label={t("name")} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label={t("role")} initialValue="operator">
            <Select options={[{ value: "operator" }, { value: "admin" }]} />
          </Form.Item>
          <Form.Item name="locale" label={t("inviteLanguage")} initialValue="de">
            <Select
              options={[
                { value: "de", label: t("langGerman") },
                { value: "en", label: t("langEnglish") },
                { value: "fr", label: t("langFrench") },
                { value: "it", label: t("langItalian") },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="admin_password"
            label={t("confirmWithPassword")}
            rules={[{ required: true }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
};

export default AdminUsers;
